import { colors } from '@puzzlio/tokens';
import type { Board, HowToPlay } from '@puzzlio/core';
import type { FieldsBoard } from './types';

/*
  A mystery field is written with a negative size, and its number is shown as a `?` — so a board that
  contains one needs a sentence the others don't.

  The check lives here rather than in the caller because the shape of a Fields board is Fields'
  business; How to Play just hands the rules whatever board is being played.
*/
function hasMysteryFields(board: Board | undefined): boolean {
  // Narrowing a Board to this game's Board. Guarded rather than asserted, because How to Play also
  // renders with no board at all, and a caller could hand over another game's.
  const fields = (board as FieldsBoard | undefined)?.fields;
  if (!Array.isArray(fields)) return false;
  return fields.some((row) => row.some((field) => parseInt(field.split(',')[1]) < 0));
}

export const howToPlay: HowToPlay = {
  game: 'fields',
  rules: ({ board }) => [
    { kind: 'line', line: 'Color every cell blue or green to create fields of the correct sizes.' },
    { kind: 'steps', items: ['Each field has a single number that shows how many cells are in that field.'] },
    ...(hasMysteryFields(board)
      ? [
          {
            kind: 'line' as const,
            line: 'Mystery fields are marked with a **?** and you have to determine the number of cells in that field.',
          },
        ]
      : []),
    {
      kind: 'steps',
      startAt: 2,
      items: ['Fields of the same color cannot touch along their edges. They may only touch diagonally.'],
    },
  ],
  cycle: [
    { fill: colors['white'] },
    { fill: colors['green-600'] },
    { fill: colors['blue-300'] },
    { fill: colors['white'] },
  ],
};
